import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useSelector } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import {
  jobLeadsApi,
  jobPostingsApi,
  jobLeadResumeUploadApi,
  jobLeadCoverLetterUploadApi,
} from "proxyUrl";
import { FaCheck } from "react-icons/fa";

// Helper function to format date string (YYYY-MM-DD) to display format (DD-MM-YYYY) in local timezone
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  // Handle both full datetime strings and date-only strings
  const datePart = dateString.slice(0, 10);
  // Parse YYYY-MM-DD in local timezone
  const [year, month, day] = datePart.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
};

// Helper function to parse date string (YYYY-MM-DD) to Date object in local timezone
const parseDateString = (dateString) => {
  if (!dateString) return null;
  // Handle both full datetime strings and date-only strings
  const datePart = dateString.slice(0, 10);
  // Parse YYYY-MM-DD in local timezone
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Helper function to format Date object to YYYY-MM-DD string in local timezone
const formatDateToISO = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultLead = {
  candidateName: "",
  email: "",
  phone: "",
  status: "New",
  stage: "New",
  desiredPay: "",
  availableStartDate: "",
  resumeUrl: "",
  coverLetterUrl: "",
  linkedin: "",
  github: "",
  portfolio: "",
  website: "",
  address: "",
  notes: "",
  source: "",
  workEligibility: "",
  relocation: "",
  noticePeriod: "",
  currentCtc: "",
  expectedCtc: "",
  references: "",
  meta: {},
};

// File Upload Component
const FileUpload = ({
  endpoint,
  fieldName,
  onUploadComplete,
  accept,
  label,
  currentUrl,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append(fieldName, file);

    try {
      const response = await axios.post(endpoint, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data) {
        const urlField =
          fieldName === "resume"
            ? "resumeUrl"
            : fieldName === "coverLetter"
            ? "coverLetterUrl"
            : `${fieldName}Url`;
        onUploadComplete(response.data[urlField] || response.data[`${fieldName}Url`]);
        toast.success("File uploaded successfully");
      }
    } catch (err) {
      console.error("File upload error", err);
      setError(err.response?.data?.message || "Upload failed");
      toast.error("File upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-gray-700">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={uploading}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue disabled:opacity-50"
        />
        {uploading && (
          <span className="text-xs text-gray-500">Uploading...</span>
        )}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {currentUrl && (
        <a
          href={currentUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-indigo-600 hover:underline"
        >
          View current file
        </a>
      )}
    </div>
  );
};

const statusOptions = [
  "New",
  "Phone Screened",
  "Reviewed",
  "Qualified",
  "Unqualified",
  "Archived",
];

const JobLeadList = () => {
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: "", status: "" });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [leadForm, setLeadForm] = useState(defaultLead);
  const [mode, setMode] = useState("create");
  const [viewPopupOpen, setViewPopupOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAvailableStartDatePicker, setShowAvailableStartDatePicker] = useState(false);

  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
  const userPermissions = loggedUser.userPermission || [];
  const canViewJob = userPermissions.includes("view Job posting");
  const canCreateLead = userPermissions.includes("create leads");
  const canUpdateLead = userPermissions.includes("update leads");
  const canDeleteLead = userPermissions.includes("delete leads");

  const fetchJob = async () => {
    try {
      const { data } = await axios.get(`${jobPostingsApi}/${jobId}`, {
        withCredentials: true,
      });
      setJob(data?.data || null);
    } catch (error) {
      console.error("jobLead.job fetch error", error);
      toast.error("Unable to load job details");
    }
  };

  // Helper function to get enabled questions (similar to backend getEnabledQuestions)
  const getEnabledQuestions = (applicationQuestions) => {
    if (!applicationQuestions || typeof applicationQuestions !== 'object') {
      return {};
    }
    
    // If applicationQuestions is already processed (has enabled flags), return as is
    // Check if it already has the structure we need
    if (applicationQuestions.resume && typeof applicationQuestions.resume === 'object' && 'enabled' in applicationQuestions.resume) {
      // Already processed format
      return applicationQuestions;
    }
    
    // Otherwise, process it to extract enabled questions
    const enabled = {};
    
    // Check each question type
    const questionTypes = [
      'resume',
      'linkedin',
      'github',
      'portfolio',
      'coverLetter',
      'workEligibility',
      'relocation',
      'noticePeriod',
      'currentCtc',
      'expectedCtc',
      'references'
    ];
    
    questionTypes.forEach((type) => {
      const question = applicationQuestions[type];
      // Handle different formats: object with enabled property, boolean, or string
      if (question) {
        if (typeof question === 'object' && question.enabled !== undefined) {
          if (question.enabled === true || question.enabled === 'true') {
            enabled[type] = { enabled: true };
          }
        } else if (question === true || question === 'true') {
          enabled[type] = { enabled: true };
        }
      }
    });
    
    return enabled;
  };

  const enabledQuestions = useMemo(() => {
    if (!job?.applicationQuestions) return {};
    return getEnabledQuestions(job.applicationQuestions);
  }, [job]);

  // Close date pickers when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!target.closest('.react-calendar') && !target.closest('input[readonly]')) {
        if (showAvailableStartDatePicker) setShowAvailableStartDatePicker(false);
      }
    };

    if (showAvailableStartDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showAvailableStartDatePicker]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const { data } = await axios.get(
        `${jobPostingsApi}/${jobId}/leads`,
        {
          params,
          withCredentials: true,
        }
      );
      setLeads(data?.data || []);
    } catch (error) {
      console.error("jobLead.list ui error", error);
      toast.error("Unable to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJob();
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const pipelineStages = useMemo(
    () => job?.pipeline?.map((p) => p.name) || ["New"],
    [job]
  );

  const PipelineIcons = ({ currentStage }) => {
    if (!job?.pipeline || job.pipeline.length === 0) {
      return <span className="text-sm text-gray-500">-</span>;
    }

    const currentStageIndex = pipelineStages.findIndex(
      (stage) => stage === currentStage
    );
    const completedCount = currentStageIndex >= 0 ? currentStageIndex + 1 : 0;

    return (
      <div className="flex items-center gap-1">
        {job.pipeline.map((stage, index) => {
          const isCompleted = index < completedCount;
          return (
            <div
              key={`${stage.name}-${index}`}
              className={`group relative flex h-5 w-5 items-center justify-center rounded-full border ${
                isCompleted
                  ? "border-green-500 bg-green-100"
                  : "border-gray-300 bg-gray-50"
              } cursor-pointer transition-all hover:scale-110`}
              title={stage.name}
            >
              {isCompleted ? (
                <FaCheck className="h-2.5 w-2.5 text-green-600" />
              ) : (
                <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
              )}
              <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                {stage.name}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const openCreateDrawer = () => {
    if (!canCreateLead) {
      toast.error("You do not have permission to create leads");
      return;
    }
    setMode("create");
    setLeadForm({
      ...defaultLead,
      stage: pipelineStages[0] || "New",
    });
    setDrawerOpen(true);
  };

  const openEditDrawer = (lead) => {
    if (!canUpdateLead) {
      toast.error("You do not have permission to update leads");
      return;
    }
    setMode("edit");
    setLeadForm({
      ...defaultLead,
      ...lead,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setLeadForm(defaultLead);
  };

  const openViewPopup = (lead) => {
    setSelectedLead(lead);
    setViewPopupOpen(true);
  };

  const closeViewPopup = () => {
    setViewPopupOpen(false);
    setSelectedLead(null);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (mode === "create" && !canCreateLead) {
      toast.error("You do not have permission to create leads");
      return;
    }
    if (mode === "edit" && !canUpdateLead) {
      toast.error("You do not have permission to update leads");
      return;
    }
    if (!leadForm.candidateName) {
      toast.error("Candidate name is required");
      return;
    }

    // Validate required fields based on enabled questions
    if (enabledQuestions.resume?.enabled && !leadForm.resumeUrl) {
      toast.error("Resume is required");
      return;
    }

    if (enabledQuestions.coverLetter?.enabled && !leadForm.coverLetterUrl) {
      toast.error("Cover Letter is required");
      return;
    }

    // Build payload with only enabled fields
    const payload = {
      jobId: jobId,
      candidateName: leadForm.candidateName,
      email: leadForm.email || null,
      phone: leadForm.phone || null,
      status: leadForm.status,
      stage: leadForm.stage,
      desiredPay: leadForm.desiredPay || null,
      availableStartDate: leadForm.availableStartDate || null,
      website: leadForm.website || null,
      address: leadForm.address || null,
      notes: leadForm.notes || null,
      source: leadForm.source || null,
      // Only include fields that are enabled
      ...(enabledQuestions.resume?.enabled && { resumeUrl: leadForm.resumeUrl }),
      ...(enabledQuestions.linkedin?.enabled && { linkedin: leadForm.linkedin || null }),
      ...(enabledQuestions.github?.enabled && { github: leadForm.github || null }),
      ...(enabledQuestions.portfolio?.enabled && { portfolio: leadForm.portfolio || null }),
      ...(enabledQuestions.coverLetter?.enabled && { coverLetterUrl: leadForm.coverLetterUrl }),
      ...(enabledQuestions.workEligibility?.enabled && { workEligibility: leadForm.workEligibility || null }),
      ...(enabledQuestions.relocation?.enabled && { relocation: leadForm.relocation || null }),
      ...(enabledQuestions.noticePeriod?.enabled && { noticePeriod: leadForm.noticePeriod || null }),
      ...(enabledQuestions.currentCtc?.enabled && { currentCtc: leadForm.currentCtc || null }),
      ...(enabledQuestions.expectedCtc?.enabled && { expectedCtc: leadForm.expectedCtc || null }),
      ...(enabledQuestions.references?.enabled && { references: leadForm.references || null }),
    };

    try {
      if (mode === "create") {
        await axios.post(
          `${jobPostingsApi}/${jobId}/leads`,
          payload,
          { withCredentials: true }
        );
        toast.success("Lead created");
      } else {
        await axios.put(`${jobLeadsApi}/${leadForm.id}`, payload, {
          withCredentials: true,
        });
        toast.success("Lead updated");
      }
      closeDrawer();
      fetchLeads();
    } catch (error) {
      console.error("jobLead.save ui error", error);
      toast.error("Unable to save lead");
    }
  };

  const handleDelete = async (leadId) => {
    if (!canDeleteLead) {
      toast.error("You do not have permission to delete leads");
      return;
    }
    const confirmDelete = window.confirm("Delete this lead?");
    if (!confirmDelete) return;
    try {
      await axios.delete(`${jobLeadsApi}/${leadId}`, {
        withCredentials: true,
      });
      toast.success("Lead deleted");
      setLeads((prev) => prev.filter((item) => item.id !== leadId));
    } catch (error) {
      console.error("jobLead.delete ui error", error);
      toast.error("Unable to delete lead");
    }
  };

  const filteredLeads = useMemo(() => leads, [leads]);

  if (!canViewJob) {
    return (
      <div className="rounded-xl bg-white p-6 text-center text-sm text-red-600 shadow">
        You are not authorized to view job postings or leads.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between rounded-2xl bg-indigo-600 p-4 text-white shadow">
        <div>
          <p className="text-xs uppercase tracking-wide opacity-80">
            Leads for
          </p>
          <h1 className="text-xl font-semibold">
            {job?.title || "Job posting"}
          </h1>
          <p className="text-xs opacity-80">
            Pipeline: {pipelineStages.join(" • ")}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/admin/job-postings/${jobId}/preview`)}
            className="rounded-lg border border-white px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
          >
            Job preview
          </button>
          {canCreateLead && (
            <button
              onClick={openCreateDrawer}
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-indigo-600 shadow hover:shadow-md"
            >
              Add lead
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 rounded-xl bg-white p-4 shadow-sm">
        <input
          type="text"
          placeholder="Search by candidate name"
          value={filters.search}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, search: e.target.value }))
          }
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue md:w-1/3"
        />
        <select
          value={filters.status}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, status: e.target.value }))
          }
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue md:w-40"
        >
          <option value="">All status</option>
          {statusOptions.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <button
            onClick={fetchLeads}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-white shadow hover:shadow-md"
          >
            Apply
          </button>
          <button
            onClick={() => {
              setFilters({ search: "", status: "" });
              fetchLeads();
            }}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#444]">
                  Candidate
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#444]">
                  Stage
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#444]">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#444]">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#444]">
                  Source
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-[#444]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {!loading && filteredLeads.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    No leads yet.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-sm text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              )}
              {filteredLeads.map((lead) => (
                <tr key={lead.id}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-indigo-600">
                        {(lead.candidateName || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <button
                          onClick={() => openViewPopup(lead)}
                          className="font-semibold text-gray-800 hover:text-indigo-600 hover:underline cursor-pointer"
                        >
                          {lead.candidateName}
                        </button>
                        <p className="text-xs text-gray-500">#{lead.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <PipelineIcons currentStage={lead.stage} />
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        lead.status === "Qualified"
                          ? "bg-green-100 text-green-700"
                          : lead.status === "Unqualified"
                          ? "bg-red-100 text-red-700"
                          : lead.status === "Archived"
                          ? "bg-gray-100 text-gray-700"
                          : "bg-indigo-100 text-indigo-700"
                      }`}
                    >
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div className="space-y-1">
                      {lead.email && <div>{lead.email}</div>}
                      {lead.phone && <div>{lead.phone}</div>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {lead.source || "-"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2 text-sm">
                      {lead.resumeUrl && (
                        <a
                          href={lead.resumeUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-gray-700 hover:bg-gray-50"
                        >
                          Resume
                        </a>
                      )}
                      {canUpdateLead && (
                        <button
                          onClick={() => openEditDrawer(lead)}
                          className="rounded-lg border border-yellow-200 px-3 py-1.5 text-yellow-700 hover:bg-yellow-50"
                        >
                          Edit
                        </button>
                      )}
                      {canDeleteLead && (
                        <button
                          onClick={() => handleDelete(lead.id)}
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-red-700 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/40">
          <div className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-[#444]">
                  {mode === "create" ? "Create lead" : "Edit lead"}
                </p>
                <h3 className="text-lg font-semibold text-gray-800">
                  {job?.title}
                </h3>
              </div>
              <button
                onClick={closeDrawer}
                className="rounded-full border border-gray-200 px-3 py-1 text-sm text-gray-700"
              >
                Close
              </button>
            </div>

            <form onSubmit={handleLeadSubmit} className="mt-4 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Candidate name*
                  </label>
                  <input
                    type="text"
                    value={leadForm.candidateName}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        candidateName: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={leadForm.phone}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Status
                  </label>
                  <select
                    value={leadForm.status}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, status: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  >
                    {statusOptions.map((item) => (
                      <option key={item}>{item}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Pipeline stage
                  </label>
                  <select
                    value={leadForm.stage}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, stage: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  >
                    {pipelineStages.map((stage) => (
                      <option key={stage}>{stage}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Source
                  </label>
                  <input
                    type="text"
                    value={leadForm.source}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, source: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Desired pay
                  </label>
                  <input
                    type="text"
                    value={leadForm.desiredPay}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        desiredPay: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Available start date
                  </label>
                  <div className="relative mt-1">
                    <input
                      type="text"
                      readOnly
                      value={formatDateForDisplay(leadForm.availableStartDate)}
                      onClick={() => {
                        setShowAvailableStartDatePicker(!showAvailableStartDatePicker);
                      }}
                      className="w-full rounded-lg border border-gray-200 pl-3 pr-11 py-2.5 text-xs text-gray-900 focus:border-primaryBlue focus:outline-none focus:ring-2 focus:ring-primaryBlue/20 transition-colors cursor-pointer"
                      placeholder=""
                    />
                    {!leadForm.availableStartDate && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 pr-11">
                        <span className="text-xs text-gray-400">Select date</span>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <svg className="h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {showAvailableStartDatePicker && (
                      <div className="absolute top-full left-0 z-50 mt-2 p-2.5">
                        <Calendar
                          onChange={(date) => {
                            const formattedDate = date ? formatDateToISO(date) : '';
                            setLeadForm((prev) => ({
                              ...prev,
                              availableStartDate: formattedDate,
                            }));
                            setShowAvailableStartDatePicker(false);
                          }}
                          value={parseDateString(leadForm.availableStartDate)}
                          formatShortWeekday={(locale, date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]}
                          prevLabel={<span className="text-purple-600">‹</span>}
                          nextLabel={<span className="text-purple-600">›</span>}
                          prev2Label={null}
                          next2Label={null}
                          tileClassName={({ date, view }) => {
                            if (view === 'month') {
                              const day = date.getDay();
                              if (day === 0 || day === 6) {
                                return 'weekend-day';
                              }
                            }
                            return null;
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Conditional Application Question Fields */}
              {enabledQuestions.resume?.enabled && (
                <div>
                  <FileUpload
                    endpoint={jobLeadResumeUploadApi}
                    fieldName="resume"
                    onUploadComplete={(url) =>
                      setLeadForm((prev) => ({ ...prev, resumeUrl: url }))
                    }
                    accept=".pdf,.doc,.docx"
                    label="Resume *"
                    currentUrl={leadForm.resumeUrl}
                  />
                </div>
              )}

              {enabledQuestions.linkedin?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    LinkedIn URL
                  </label>
                  <input
                    type="url"
                    value={leadForm.linkedin}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, linkedin: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
              )}

              {enabledQuestions.github?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    GitHub URL
                  </label>
                  <input
                    type="url"
                    value={leadForm.github}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, github: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    placeholder="https://github.com/username"
                  />
                </div>
              )}

              {enabledQuestions.portfolio?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Portfolio URL
                  </label>
                  <input
                    type="url"
                    value={leadForm.portfolio}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        portfolio: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    placeholder="https://portfolio.com"
                  />
                </div>
              )}

              {enabledQuestions.coverLetter?.enabled && (
                <div>
                  <FileUpload
                    endpoint={jobLeadCoverLetterUploadApi}
                    fieldName="coverLetter"
                    onUploadComplete={(url) =>
                      setLeadForm((prev) => ({ ...prev, coverLetterUrl: url }))
                    }
                    accept=".pdf,.doc,.docx"
                    label="Cover Letter *"
                    currentUrl={leadForm.coverLetterUrl}
                  />
                </div>
              )}

              {enabledQuestions.workEligibility?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Work Eligibility
                  </label>
                  <select
                    value={leadForm.workEligibility}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        workEligibility: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  >
                    <option value="">Select</option>
                    <option value="Authorized to work">Authorized to work</option>
                    <option value="Requires sponsorship">
                      Requires sponsorship
                    </option>
                    <option value="Not authorized">Not authorized</option>
                  </select>
                </div>
              )}

              {enabledQuestions.relocation?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Willing to Relocate?
                  </label>
                  <select
                    value={leadForm.relocation}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        relocation: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Maybe">Maybe</option>
                  </select>
                </div>
              )}

              {enabledQuestions.noticePeriod?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Notice Period
                  </label>
                  <input
                    type="text"
                    value={leadForm.noticePeriod}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        noticePeriod: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    placeholder="e.g., 2 weeks, 1 month"
                  />
                </div>
              )}

              {enabledQuestions.currentCtc?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Current CTC
                  </label>
                  <input
                    type="text"
                    value={leadForm.currentCtc}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        currentCtc: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    placeholder="e.g., $50,000"
                  />
                </div>
              )}

              {enabledQuestions.expectedCtc?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    Expected CTC
                  </label>
                  <input
                    type="text"
                    value={leadForm.expectedCtc}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        expectedCtc: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    placeholder="e.g., $60,000"
                  />
                </div>
              )}

              {enabledQuestions.references?.enabled && (
                <div>
                  <label className="text-xs font-semibold text-gray-700">
                    References
                  </label>
                  <textarea
                    rows={4}
                    value={leadForm.references}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        references: e.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                    placeholder="Name, Company, Email, Phone"
                  />
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-700">
                  Website / Portfolio
                </label>
                <input
                  type="text"
                  value={leadForm.website}
                  onChange={(e) =>
                    setLeadForm((prev) => ({
                      ...prev,
                      website: e.target.value,
                    }))
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                  placeholder="https://"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">
                  Address
                </label>
                <input
                  type="text"
                  value={leadForm.address}
                  onChange={(e) =>
                    setLeadForm((prev) => ({ ...prev, address: e.target.value }))
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">
                  Notes
                </label>
                <textarea
                  rows={3}
                  value={leadForm.notes}
                  onChange={(e) =>
                    setLeadForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    (mode === "create" && !canCreateLead) ||
                    (mode === "edit" && !canUpdateLead)
                  }
                  className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:shadow-md disabled:opacity-60"
                >
                  {mode === "create" ? "Create lead" : "Update lead"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {viewPopupOpen && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {selectedLead.candidateName}
                </h2>
                <p className="text-sm text-gray-500">
                  Application ID: #{selectedLead.id}
                </p>
              </div>
              <button
                onClick={closeViewPopup}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="p-6">
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase text-[#444]">
                      Personal Information
                    </h3>
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Candidate Name
                        </p>
                        <p className="mt-1 text-sm font-medium text-gray-800">
                          {selectedLead.candidateName || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Email
                        </p>
                        <p className="mt-1 text-sm text-gray-800">
                          {selectedLead.email || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Phone
                        </p>
                        <p className="mt-1 text-sm text-gray-800">
                          {selectedLead.phone || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Address
                        </p>
                        <p className="mt-1 text-sm text-gray-800">
                          {selectedLead.address || "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase text-[#444]">
                      Application Details
                    </h3>
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Status
                        </p>
                        <span
                          className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            selectedLead.status === "Qualified"
                              ? "bg-green-100 text-green-700"
                              : selectedLead.status === "Unqualified"
                              ? "bg-red-100 text-red-700"
                              : selectedLead.status === "Archived"
                              ? "bg-gray-100 text-gray-700"
                              : "bg-indigo-100 text-indigo-700"
                          }`}
                        >
                          {selectedLead.status || "-"}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Pipeline Stage
                        </p>
                        <div className="mt-2">
                          <PipelineIcons currentStage={selectedLead.stage} />
                        </div>
                        <p className="mt-1 text-sm text-gray-800">
                          {selectedLead.stage || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Source
                        </p>
                        <p className="mt-1 text-sm text-gray-800">
                          {selectedLead.source || "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase text-[#444]">
                      Job Preferences
                    </h3>
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Desired Pay
                        </p>
                        <p className="mt-1 text-sm text-gray-800">
                          {selectedLead.desiredPay || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500">
                          Available Start Date
                        </p>
                        <p className="mt-1 text-sm text-gray-800">
                          {selectedLead.availableStartDate
                            ? new Date(
                                selectedLead.availableStartDate
                              ).toLocaleDateString()
                            : "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase text-[#444]">
                      Links & Resources
                    </h3>
                    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                      {enabledQuestions.resume?.enabled && selectedLead.resumeUrl && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500">
                            Resume
                          </p>
                          <a
                            href={selectedLead.resumeUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 block text-sm text-indigo-600 hover:underline"
                          >
                            Download Resume
                          </a>
                        </div>
                      )}
                      {enabledQuestions.linkedin?.enabled && selectedLead.linkedin && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500">
                            LinkedIn
                          </p>
                          <a
                            href={selectedLead.linkedin}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 block text-sm text-indigo-600 hover:underline"
                          >
                            {selectedLead.linkedin}
                          </a>
                        </div>
                      )}
                      {enabledQuestions.github?.enabled && selectedLead.github && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500">
                            GitHub
                          </p>
                          <a
                            href={selectedLead.github}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 block text-sm text-indigo-600 hover:underline"
                          >
                            {selectedLead.github}
                          </a>
                        </div>
                      )}
                      {enabledQuestions.portfolio?.enabled && selectedLead.portfolio && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500">
                            Portfolio
                          </p>
                          <a
                            href={selectedLead.portfolio}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 block text-sm text-indigo-600 hover:underline"
                          >
                            {selectedLead.portfolio}
                          </a>
                        </div>
                      )}
                      {enabledQuestions.coverLetter?.enabled && selectedLead.coverLetterUrl && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500">
                            Cover Letter
                          </p>
                          <a
                            href={selectedLead.coverLetterUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 block text-sm text-indigo-600 hover:underline"
                          >
                            Download Cover Letter
                          </a>
                        </div>
                      )}
                      {selectedLead.website && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500">
                            Website
                          </p>
                          <a
                            href={selectedLead.website}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 block text-sm text-indigo-600 hover:underline"
                          >
                            {selectedLead.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {(enabledQuestions.workEligibility?.enabled ||
                    enabledQuestions.relocation?.enabled ||
                    enabledQuestions.noticePeriod?.enabled ||
                    enabledQuestions.currentCtc?.enabled ||
                    enabledQuestions.expectedCtc?.enabled ||
                    enabledQuestions.references?.enabled) && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold uppercase text-[#444]">
                        Additional Information
                      </h3>
                      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        {enabledQuestions.workEligibility?.enabled &&
                          selectedLead.workEligibility && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500">
                                Work Eligibility
                              </p>
                              <p className="mt-1 text-sm text-gray-800">
                                {selectedLead.workEligibility}
                              </p>
                            </div>
                          )}
                        {enabledQuestions.relocation?.enabled &&
                          selectedLead.relocation && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500">
                                Relocation
                              </p>
                              <p className="mt-1 text-sm text-gray-800">
                                {selectedLead.relocation}
                              </p>
                            </div>
                          )}
                        {enabledQuestions.noticePeriod?.enabled &&
                          selectedLead.noticePeriod && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500">
                                Notice Period
                              </p>
                              <p className="mt-1 text-sm text-gray-800">
                                {selectedLead.noticePeriod}
                              </p>
                            </div>
                          )}
                        {enabledQuestions.currentCtc?.enabled &&
                          selectedLead.currentCtc && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500">
                                Current CTC
                              </p>
                              <p className="mt-1 text-sm text-gray-800">
                                {selectedLead.currentCtc}
                              </p>
                            </div>
                          )}
                        {enabledQuestions.expectedCtc?.enabled &&
                          selectedLead.expectedCtc && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500">
                                Expected CTC
                              </p>
                              <p className="mt-1 text-sm text-gray-800">
                                {selectedLead.expectedCtc}
                              </p>
                            </div>
                          )}
                        {enabledQuestions.references?.enabled &&
                          selectedLead.references && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500">
                                References
                              </p>
                              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
                                {selectedLead.references}
                              </p>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                  {selectedLead.notes && (
                    <div>
                      <h3 className="mb-3 text-sm font-semibold uppercase text-[#444]">
                        Notes
                      </h3>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {selectedLead.notes}
                        </p>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase text-[#444]">
                      Timestamps
                    </h3>
                    <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
                      {selectedLead.createdAt && (
                        <p>
                          Created:{" "}
                          {new Date(selectedLead.createdAt).toLocaleString()}
                        </p>
                      )}
                      {selectedLead.updatedAt && (
                        <p>
                          Updated:{" "}
                          {new Date(selectedLead.updatedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  onClick={closeViewPopup}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                {canUpdateLead && (
                  <button
                    onClick={() => {
                      closeViewPopup();
                      openEditDrawer(selectedLead);
                    }}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600/90"
                  >
                    Edit Lead
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <Link
          to="/admin/job-postings"
          className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700"
        >
          Back to postings
        </Link>
        <button
          onClick={() => navigate(`/admin/job-postings/${jobId}/preview`)}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white"
        >
          Preview posting
        </button>
      </div>
      <ToastContainer />
    </div>
  );
};

export default JobLeadList;

